import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import UndoIcon from '@mui/icons-material/Undo';
import { useBrand } from '../contexts/BrandContext';
import { api } from '../services/api';
import { auditsApi } from '../services/audits';
import { proposedArticlesApi } from '../services/proposed-articles';
import { SuggestionRow } from '../components/strategy/SuggestionRow';
import type {
  Audit,
  ProposedArticle,
  ProposedArticleStatus,
  NewsKeywordSuggestion,
} from '@traaker/shared';

type TabKey = ProposedArticleStatus;

const TABS: { key: TabKey; label: string }[] = [
  { key: 'proposed', label: 'À examiner' },
  { key: 'saved', label: 'Enregistrées' },
  { key: 'published', label: 'Publiées' },
  { key: 'dismissed', label: 'Archivées' },
];

function formatRelative(iso?: string): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days <= 0) return "aujourd'hui";
  if (days === 1) return 'hier';
  if (days < 7) return `il y a ${days} jours`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `il y a ${weeks} semaine${weeks > 1 ? 's' : ''}`;
  const months = Math.floor(days / 30);
  return `il y a ${months} mois`;
}

function domainOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export default function NewsWatch() {
  const { activeBrand, refreshBrands } = useBrand();
  const [tab, setTab] = useState<TabKey>('proposed');
  const [articles, setArticles] = useState<ProposedArticle[]>([]);
  const [suggestions, setSuggestions] = useState<NewsKeywordSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [launching, setLaunching] = useState(false);
  const [lastRunAt, setLastRunAt] = useState<string | undefined>();
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [publishDialog, setPublishDialog] = useState<ProposedArticle | null>(null);
  const [publishedUrl, setPublishedUrl] = useState('');

  const load = useCallback(async () => {
    if (!activeBrand?.brandId) {
      setArticles([]);
      setSuggestions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [list, suggs, newsAudits] = await Promise.all([
        proposedArticlesApi.list(activeBrand.brandId, tab),
        proposedArticlesApi.getSuggestedKeywords(activeBrand.brandId).catch(() => []),
        auditsApi.list('news_watch', activeBrand.brandId).catch(() => [] as Audit[]),
      ]);
      setArticles(list);
      setSuggestions(suggs);
      const completed = newsAudits.filter((a) => a.status === 'completed').sort(
        (a, b) => (b.completedAt || '').localeCompare(a.completedAt || ''),
      );
      setLastRunAt(completed[0]?.completedAt);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [activeBrand?.brandId, tab]);

  useEffect(() => {
    load();
  }, [load]);

  const handleLaunch = async () => {
    if (!activeBrand?.brandId) return;
    setLaunching(true);
    setError('');
    try {
      await auditsApi.create('news_watch', {}, activeBrand.brandId);
      setFeedbackMessage('Veille lancée — les idées apparaîtront dans quelques minutes.');
      setTimeout(() => setFeedbackMessage(''), 5000);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du lancement de la veille');
    } finally {
      setLaunching(false);
    }
  };

  const handleStatusChange = async (
    article: ProposedArticle,
    status: ProposedArticleStatus,
    url?: string,
  ) => {
    try {
      await proposedArticlesApi.updateStatus(article.articleId, {
        brandId: article.brandId,
        status,
        publishedUrl: url,
      });
      await load();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la mise à jour');
    }
  };

  const handleAddKeyword = async (tag: string) => {
    if (!activeBrand) return;
    const keywords = [...(activeBrand.strategy?.keywords || []), tag];
    await api.patch(`/brands/${activeBrand.brandId}/strategy`, { keywords });
    setFeedbackMessage(`"${tag}" ajouté à votre stratégie`);
    setTimeout(() => setFeedbackMessage(''), 3000);
    await refreshBrands();
    await load();
  };

  const handleRemoveKeyword = async (tag: string) => {
    if (!activeBrand) return;
    const keywords = (activeBrand.strategy?.keywords || []).filter((x) => x !== tag);
    await api.patch(`/brands/${activeBrand.brandId}/strategy`, { keywords });
    setFeedbackMessage(`"${tag}" retiré`);
    setTimeout(() => setFeedbackMessage(''), 3000);
    await refreshBrands();
  };

  const openPublishDialog = (article: ProposedArticle) => {
    setPublishDialog(article);
    setPublishedUrl(article.publishedUrl || '');
  };

  const confirmPublish = async () => {
    if (!publishDialog) return;
    const url = publishedUrl.trim() || undefined;
    await handleStatusChange(publishDialog, 'published', url);
    setPublishDialog(null);
    setPublishedUrl('');
  };

  const filteredSuggestions = useMemo(
    () => suggestions.filter((s) => !activeBrand?.strategy?.keywords?.some((k) => k.toLowerCase() === s.tag.toLowerCase())),
    [suggestions, activeBrand?.strategy?.keywords],
  );

  if (!activeBrand) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="info">Sélectionnez une marque pour voir la veille news.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Idées d'articles</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Nouvelles suggestions publiées régulièrement en fonction de votre stratégie de marque.
          </Typography>
          {lastRunAt && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Dernière veille : {formatRelative(lastRunAt)}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={launching ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
          onClick={handleLaunch}
          disabled={launching}
        >
          Lancer une veille news
        </Button>
      </Box>

      {feedbackMessage && <Alert severity="success" sx={{ mb: 2 }}>{feedbackMessage}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Appear-more-on-these-topics card (feature C) */}
      {filteredSuggestions.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Apparaître plus sur ces sujets
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Vous avez enregistré plusieurs idées sur ces sujets — ajoutez-les à votre stratégie pour affiner vos prochaines veilles.
            </Typography>
            {filteredSuggestions.map((s) => (
              <SuggestionRow
                key={s.tag}
                label="Sujet"
                value={s.tag}
                color="#1565C0"
                alreadyAdded={activeBrand?.strategy?.keywords?.includes(s.tag) || false}
                onAdd={() => handleAddKeyword(s.tag)}
                onRemove={() => handleRemoveKeyword(s.tag)}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        {TABS.map((t) => (
          <Tab key={t.key} value={t.key} label={t.label} />
        ))}
      </Tabs>

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : articles.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body1" color="text.secondary">
              {tab === 'proposed'
                ? 'Aucune idée pour le moment. Lancez une veille pour générer des propositions.'
                : 'Aucune idée dans cet onglet.'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {articles.map((article) => (
            <ArticleCard
              key={article.articleId}
              article={article}
              tab={tab}
              onSave={() => handleStatusChange(article, 'saved')}
              onDismiss={() => handleStatusChange(article, 'dismissed')}
              onRestore={() => handleStatusChange(article, 'proposed')}
              onMarkPublished={() => openPublishDialog(article)}
            />
          ))}
        </Stack>
      )}

      {/* Publish dialog */}
      <Dialog open={!!publishDialog} onClose={() => setPublishDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Marquer comme publié</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ajoutez le lien de l'article publié (optionnel).
          </Typography>
          <TextField
            fullWidth
            label="URL de l'article publié"
            placeholder="https://..."
            value={publishedUrl}
            onChange={(e) => setPublishedUrl(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDialog(null)}>Annuler</Button>
          <Button variant="contained" onClick={confirmPublish}>Confirmer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function ArticleCard({
  article,
  tab,
  onSave,
  onDismiss,
  onRestore,
  onMarkPublished,
}: {
  article: ProposedArticle;
  tab: TabKey;
  onSave: () => void;
  onDismiss: () => void;
  onRestore: () => void;
  onMarkPublished: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const competitors = article.competitorMentions || [];

  return (
    <Card>
      <CardContent>
        {/* Title + tags */}
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>{article.title}</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
          {(article.topicTags || []).map((t) => (
            <Chip key={t} label={t} size="small" sx={{ bgcolor: '#E3F2FD', color: '#1565C0' }} />
          ))}
        </Box>

        {/* Competitor-mention badge (feature A) */}
        {competitors.length > 0 && (
          <Tooltip title={competitors.join(', ')}>
            <Chip
              icon={<WarningAmberIcon />}
              label={
                competitors.length === 1
                  ? `Votre concurrent ${competitors[0]} est cité`
                  : `${competitors.length} concurrents cités`
              }
              size="small"
              color="warning"
              sx={{ mb: 1.5, fontWeight: 600 }}
            />
          </Tooltip>
        )}

        {/* Angle */}
        {article.proposedAngle && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Angle
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>{article.proposedAngle}</Typography>
          </Box>
        )}

        {/* Expandable digest + sources */}
        <Button
          size="small"
          onClick={() => setExpanded(!expanded)}
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ textTransform: 'none' }}
        >
          Ce qu'il se passe
        </Button>
        <Collapse in={expanded}>
          <Box sx={{ mt: 1, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            {article.newsDigest && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                {article.newsDigest}
              </Typography>
            )}
            {(article.sourceUrls || []).length > 0 && (
              <Stack spacing={0.75}>
                {(article.sourceUrls || []).map((src, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label={src.domain || domainOf(src.url)} size="small" variant="outlined" />
                    <Link href={src.url} target="_blank" rel="noopener" sx={{ fontSize: 13, flex: 1 }}>
                      {src.title || src.url}
                    </Link>
                    <IconButton size="small" href={src.url} target="_blank" rel="noopener">
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </Collapse>

        {/* Published info */}
        {tab === 'published' && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: '#E8F5E9', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Publié le {article.publishedAt ? new Date(article.publishedAt).toLocaleDateString('fr-FR') : '—'}
            </Typography>
            {article.publishedUrl && (
              <Box>
                <Link href={article.publishedUrl} target="_blank" rel="noopener" sx={{ fontSize: 13 }}>
                  {article.publishedUrl}
                </Link>
              </Box>
            )}
          </Box>
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          {tab === 'proposed' && (
            <>
              <Button size="small" variant="outlined" onClick={onDismiss}>Ignorer</Button>
              <Button size="small" variant="contained" onClick={onSave}>Enregistrer</Button>
            </>
          )}
          {tab === 'saved' && (
            <>
              <Button size="small" variant="outlined" onClick={onDismiss}>Ignorer</Button>
              <Button size="small" variant="contained" onClick={onMarkPublished}>Marquer comme publié</Button>
            </>
          )}
          {tab === 'dismissed' && (
            <Button size="small" variant="outlined" startIcon={<UndoIcon />} onClick={onRestore}>
              Restaurer
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
